import { useEffect, useState } from 'react'

import { getJson, postJson } from '@/api/client'
import type { ShadowConversationDetail, ShadowRunResult } from '@/api/types'
import { TurnComparisonTable } from '@/admin/TurnComparisonTable'
import { Badge } from '@/ui/primitives'
import { Button } from '@/ui/Button'
import { toast } from '@/ui/Toast'

interface Props {
  convId: number
  onBack: () => void
}

function catBadge(cat: string | null): 'default' | 'success' | 'warning' | 'danger' {
  if (cat === 'complaint') return 'danger'
  if (cat === 'order_status') return 'warning'
  if (cat === 'beauty') return 'success'
  return 'default'
}

function catLabel(cat: string | null): string {
  if (!cat) return ''
  const m: Record<string, string> = {
    beauty: 'Beauty', order_status: 'Order', complaint: 'Complaint',
    pricing: 'Pricing', product_availability: 'Avail.', store_info: 'Store',
    how_to_use: 'How-to', social_media: 'Social', general: 'General',
  }
  return m[cat] || cat
}

export function ShadowComparison({ convId, onBack }: Props) {
  const [detail, setDetail] = useState<ShadowConversationDetail | null>(null)
  const [rerunning, setRerunning] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => { void loadDetail() }, [convId])

  async function loadDetail() {
    try {
      const data = await getJson<ShadowConversationDetail>(`/v1/admin/shadow/conversations/${convId}`)
      setDetail(data)
      setError(null)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  async function handleRerun() {
    setRerunning(true)
    setError(null)
    try {
      const result = await postJson<ShadowRunResult>('/v1/admin/shadow/run', {
        conversation_ids: [convId], limit: null,
      })
      toast.success(`Re-ran: ${result.total_turns_ran} turns, cost $${result.total_cost_usd.toFixed(4)}`)
      await loadDetail()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    } finally { setRerunning(false) }
  }

  if (!detail) {
    return (
      <div className="flex items-center justify-center min-h-screen text-mute text-sm">loading…</div>
    )
  }

  const conv = detail.conversation

  return (
    <div className="max-w-full mx-auto px-6 lg:px-10 py-12 h-full overflow-y-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <button onClick={onBack} className="text-xs text-mute hover:text-cream transition-colors mb-2 block">
            ← Back to Shadow Testing
          </button>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="font-display text-3xl font-medium text-cream tracking-tight">
              {conv.customer_name || `User #${conv.remote_from_id}`}
            </h1>
            {conv.primary_category && (
              <Badge variant={catBadge(conv.primary_category)}>{catLabel(conv.primary_category)}</Badge>
            )}
          </div>
          <div className="text-xs text-mute">
            {conv.total_human_messages} turns · ${conv.total_cost_usd.toFixed(4)}
            {conv.customer_phone && ` · ${conv.customer_phone}`}
          </div>
        </div>
        <Button variant="ghost" size="sm" loading={rerunning} onClick={handleRerun}>Re-run All</Button>
      </div>

      {error && (
        <div className="mb-6 font-display italic text-sm border-l-[3px] border-ember text-ember pl-4 py-2 bg-ember/5">
          {error}
        </div>
      )}

      {detail.turns.length === 0 ? (
        <div className="text-center text-mute text-sm py-16">No turns in this conversation.</div>
      ) : (
        <TurnComparisonTable
          turns={detail.turns}
          convId={conv.id}
          contextJson={conv.context_json}
        />
      )}
    </div>
  )
}
